"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  HINT,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { WordSourceList } from "@/components/tools/reference-tools/word-source-list";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  collectMacros,
  parseBibtex,
} from "@/lib/tool-kit/reference-tools/bibtex";
import {
  bibEntryToWordSource,
  cslToWordSource,
  existingSources,
  sourcesXml,
  type WordSource,
} from "@/lib/tool-kit/reference-tools/word-sources";

/** English strings. A translated content file overrides these through `ui`. */
export const BIBTEX_TO_WORD_SOURCE_MANAGER_STRINGS = {
  formatLabel: "Input format",
  formatAuto: "Detect automatically",
  formatBibtex: "BibTeX (.bib)",
  formatRis: "RIS (.ris)",
  inputLabel: "Your references — paste BibTeX or RIS, or load a file",
  inputPlaceholder:
    "@article{hirsch2005,\n  author  = {Hirsch, Jorge E.},\n  title   = {An index to quantify an individual's scientific research output},\n  journal = {Proceedings of the National Academy of Sciences},\n  year    = {2005},\n  volume  = {102},\n  number  = {46},\n  pages   = {16569--16572},\n  doi     = {10.1073/pnas.0507655102}\n}",
  loadFile: "Load a .bib or .ris file",
  mergeLabel: "Optional: add to an existing Sources.xml",
  mergeHint:
    "Choose the Sources.xml you already have and the new sources are added after its own — the file you download then replaces it without losing anything.",
  mergeLoaded:
    "one:{count} existing source will be kept.|other:{count} existing sources will be kept.",
  mergeInvalid:
    "That file is not a Word bibliography file — it has no <b:Sources> element.",
  loadFailed: "That file could not be read as text.",
  convert: "Create Sources.xml",
  converting: "Reading your references…",
  emptyPrompt:
    "Paste BibTeX or RIS records, then create the file. Everything runs in your browser — nothing is uploaded.",
  noRecords:
    "No records found. BibTeX entries start with @type{key, and RIS records with TY  -.",
  risFailed:
    "The RIS records could not be read. Check that each record starts with TY  - and ends with ER  -.",
  results: "Word source file",
  summary:
    "one:{count} source ready for Word's Source Manager.|other:{count} sources ready for Word's Source Manager.",
  copy: "Copy XML",
  copied: "Copied!",
  download: "Download Sources.xml",
  nextStep:
    "Next: in Word for Windows, open References → Manage Sources → Browse… and choose this file. On a Mac, replace Word's own Sources.xml — the steps are below.",
  listHeading: "What goes into Word",
  tag: "Tag",
  wordType: "Word source type",
  from: "From",
  title: "Title",
  notesHeading: "Check",
  preview: "Show the XML",
  types: {
    Book: "Book",
    BookSection: "Book Section",
    JournalArticle: "Journal Article",
    ArticleInAPeriodical: "Article in a Periodical",
    ConferenceProceedings: "Conference Proceedings",
    Report: "Report",
    InternetSite: "Web site",
    DocumentFromInternetSite: "Document From Web site",
    ElectronicSource: "Electronic Source",
    Patent: "Patent",
    Misc: "Miscellaneous",
  },
  notes: {
    etAlDropped: "“and others” dropped — Word lists only the names it is given",
    truncated: "a field was cut to Word's 255-character limit",
    noAuthor: "no author or editor",
    noTitle: "no title",
    noYear: "no year",
    tagChanged: "tag reduced to letters, digits and underscores",
  },
} as const;

export type WordConverterStrings = typeof BIBTEX_TO_WORD_SOURCE_MANAGER_STRINGS;
type InputFormat = "auto" | "bibtex" | "ris";

const XML_MIME = "application/xml;charset=utf-8";
const RE_RIS = /^TY {2}- /m;

interface Existing {
  blocks: string[];
  tags: string[];
}

type Outcome =
  | { kind: "empty" }
  | { kind: "error"; message: string }
  | { kind: "done"; sources: WordSource[]; xml: string };

function bibtexSources(text: string, used: Set<string>): WordSource[] {
  const doc = parseBibtex(text);
  const macros = collectMacros(doc.nodes);
  return doc.nodes.flatMap((node) =>
    node.kind === "entry" ? [bibEntryToWordSource(node, macros, used)] : []
  );
}

/** RIS through the site's own importer, loaded only when RIS is converted. */
async function risSources(
  text: string,
  used: Set<string>
): Promise<WordSource[]> {
  const { editableEntryToCsl, parseBibliographyImport } = await import(
    "@/lib/citation"
  );
  const { entries } = parseBibliographyImport(text, "ris");
  return entries.map((entry, index) =>
    cslToWordSource(editableEntryToCsl(entry), index, used)
  );
}

async function convert(
  text: string,
  format: InputFormat,
  existing: Existing | null,
  strings: WordConverterStrings
): Promise<Outcome> {
  const ris = format === "ris" || (format === "auto" && RE_RIS.test(text));
  const used = new Set((existing?.tags ?? []).map((tag) => tag.toLowerCase()));
  let sources: WordSource[];
  try {
    sources = ris ? await risSources(text, used) : bibtexSources(text, used);
  } catch {
    return {
      kind: "error",
      message: ris ? strings.risFailed : strings.noRecords,
    };
  }
  if (sources.length === 0) {
    return { kind: "error", message: strings.noRecords };
  }
  return {
    kind: "done",
    sources,
    xml: sourcesXml(sources, existing?.blocks ?? []),
  };
}

export default function BibtexToWordSourceManagerWidget() {
  const strings = useToolUi(BIBTEX_TO_WORD_SOURCE_MANAGER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [format, setFormat] = useState<InputFormat>("auto");
  const [existing, setExisting] = useState<Existing | null>(null);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>({ kind: "empty" });
  const [busy, setBusy] = useState(false);

  async function readFile(file: File | undefined): Promise<string | null> {
    if (!file) {
      return null;
    }
    try {
      setLoadFailed(false);
      return await file.text();
    } catch {
      setLoadFailed(true);
      return null;
    }
  }

  async function handleInputFile(file: File | undefined) {
    const content = await readFile(file);
    if (content !== null) {
      setText(content);
    }
  }

  async function handleMergeFile(file: File | undefined) {
    const content = await readFile(file);
    if (content === null) {
      setExisting(null);
      return;
    }
    const found = existingSources(content);
    setExisting(found);
    setMergeError(found ? null : strings.mergeInvalid);
  }

  async function handleConvert() {
    setBusy(true);
    setOutcome(await convert(text, format, existing, strings));
    setBusy(false);
  }

  const done = outcome.kind === "done" ? outcome : null;

  return (
    <section className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField<InputFormat>
          id="bws-format"
          label={strings.formatLabel}
          onChange={setFormat}
          options={[
            { value: "auto", label: strings.formatAuto },
            { value: "bibtex", label: strings.formatBibtex },
            { value: "ris", label: strings.formatRis },
          ]}
          value={format}
        />
        <div className="space-y-2">
          <label className={LABEL} htmlFor="bws-file">
            {strings.loadFile}
          </label>
          <input
            accept=".bib,.bibtex,.ris,.txt,text/plain"
            className="block w-full text-foreground text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-card file:px-3 file:py-1 file:text-foreground"
            id="bws-file"
            onChange={(event) => handleInputFile(event.target.files?.[0])}
            type="file"
          />
        </div>
      </div>
      <TextAreaField
        id="bws-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={10}
        value={text}
      />
      <div className="space-y-2">
        <label className={LABEL} htmlFor="bws-merge">
          {strings.mergeLabel}
        </label>
        <input
          accept=".xml,application/xml,text/xml"
          className="block w-full text-foreground text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-card file:px-3 file:py-1 file:text-foreground"
          id="bws-merge"
          onChange={(event) => handleMergeFile(event.target.files?.[0])}
          type="file"
        />
        <p className={HINT}>{strings.mergeHint}</p>
        {existing ? (
          <p className="text-foreground text-sm">
            {tPlural(strings.mergeLoaded, locale, existing.blocks.length, {
              count: fmt(existing.blocks.length),
            })}
          </p>
        ) : null}
        {mergeError ? <ErrorNotice>{mergeError}</ErrorNotice> : null}
        {loadFailed ? <ErrorNotice>{strings.loadFailed}</ErrorNotice> : null}
      </div>
      <button
        className={BUTTON_PRIMARY}
        disabled={busy || text.trim() === ""}
        onClick={handleConvert}
        type="button"
      >
        {busy ? strings.converting : strings.convert}
      </button>
      <div aria-live="polite" className="space-y-6">
        {outcome.kind === "empty" ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : null}
        {outcome.kind === "error" ? (
          <ErrorNotice>{outcome.message}</ErrorNotice>
        ) : null}
        {done ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className={WIDGET_H2}>{strings.results}</h2>
                <p className="text-muted-foreground text-sm">
                  {tPlural(strings.summary, locale, done.sources.length, {
                    count: fmt(done.sources.length),
                  })}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  className={BUTTON_PRIMARY}
                  onClick={() =>
                    downloadText("Sources.xml", done.xml, XML_MIME)
                  }
                  type="button"
                >
                  {strings.download}
                </button>
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copy}
                  text={done.xml}
                />
              </div>
            </div>
            <p className="text-foreground text-sm">{strings.nextStep}</p>
            <WordSourceList sources={done.sources} strings={strings} />
            <details>
              <summary className="cursor-pointer text-muted-foreground text-sm">
                {strings.preview}
              </summary>
              <pre className="mt-3 max-h-96 overflow-auto rounded-lg border border-border bg-card p-4 font-mono text-foreground text-xs leading-relaxed">
                {done.xml}
              </pre>
            </details>
          </div>
        ) : null}
      </div>
    </section>
  );
}
